import { MachinePublicHydrated } from "@aura-frontend/omnibus-az-client";
import { Schedule } from "@aura-frontend/omnibus-az-client";
import { Button, Card, cn, Icons } from "@win-reality/react-ui";
import { Link, useNavigate, useParams } from "react-router-dom";

import { ROUTES } from "@/lib/routes";
import {
  type LiveEventInitialType,
  type LiveEventPrefill,
} from "@/types/eventNavigation";
import { getScorekeepUrl } from "@/utils/scorekeepingUtils";

import { FieldCardEvent } from "./FieldCardEvent";
import { FieldDevices } from "./FieldDevices";
import { UpcomingFieldCardEvent } from "./UpcomingFieldCardEvent";

export { HealthyIndicator } from "./HealthyIndicator";

type FieldCardProps = {
  machineField: MachinePublicHydrated;
  maybeUpcomingEvent?: Schedule;
};

function FieldCard({ machineField, maybeUpcomingEvent }: FieldCardProps) {
  const { complexName } = useParams<{ complexName: string }>();
  const navigate = useNavigate();

  if (!machineField) return null;

  const startLiveEvent = (type: LiveEventInitialType) => {
    const path = ROUTES.NEW_EVENT.replace(
      ":complexName",
      complexName ?? "",
    ).replace(":fieldId", machineField.machineUuid);
    const livePrefill: LiveEventPrefill = { type };
    navigate(path, { state: { livePrefill } });
  };

  const maybeUpcomingEventPath = maybeUpcomingEvent
    ? ROUTES.EDIT_EVENT.replace(":complexName", complexName ?? "")
        .replace(":fieldId", machineField.machineUuid ?? "")
        .replace(":eventId", maybeUpcomingEvent.id ?? "")
    : "#";

  // Get event type, preferring current game over upcoming event
  const eventType =
    machineField.currentGame?.type || maybeUpcomingEvent?.merged?.game?.type;

  return (
    <Card key={machineField.machineUuid} className="p-4 bg-white">
      <div className="flex justify-between">
        <div className="text-base">{machineField.name}</div>

        <div className="text-xs text-gray-400 capitalize">
          {[
            maybeUpcomingEvent?.merged?.park?.field?.ageGroup,
            maybeUpcomingEvent?.merged?.game?.sport ||
              machineField.currentGame?.sport,
          ]
            .filter(Boolean)
            .join(" · ")}
        </div>
      </div>
      <div className="py-4">
        {maybeUpcomingEvent ? (
          <Card className="shadow-glow-tiny hover:bg-blue/0! hover:shadow-glow-medium cursor-pointer transition-all">
            <Link to={maybeUpcomingEventPath}>
              <UpcomingFieldCardEvent event={maybeUpcomingEvent} />
            </Link>
          </Card>
        ) : (
          <>
            {machineField.currentGame && (
              <div className="flex flex-col gap-3">
                <Card className="shadow-glow-tiny hover:bg-blue/0! hover:shadow-glow-medium cursor-pointer transition-all">
                  <Link
                    to={ROUTES.EDIT_EVENT.replace(
                      ":complexName",
                      complexName ?? "",
                    )
                      .replace(":fieldId", machineField.machineUuid)
                      .replace(
                        ":eventId",
                        machineField.currentGame.gameUuid ?? "",
                      )}
                  >
                    <FieldCardEvent
                      event={machineField.currentGame}
                      field={machineField}
                    />
                  </Link>
                </Card>
              </div>
            )}

            {!machineField.currentGame && (
              <Card className="shadow-glow-tiny">
                <div className="grid grid-cols-2 gap-4">
                  <Button
                    variant="secondary-flat-dark"
                    size="default-square"
                    className="flex px-0 justify-around text-xs normal-case"
                    onClick={() => startLiveEvent("game")}
                  >
                    <span className="size-3" />
                    Start Game
                    <Icons.ChevronRight className="size-3 shrink-0" />
                  </Button>

                  <Button
                    variant="secondary-flat-dark"
                    size="default-square"
                    className="flex px-0 justify-around text-xs normal-case"
                    onClick={() => startLiveEvent("practice")}
                  >
                    <span className="size-3" />
                    Start Practice
                    <Icons.ChevronRight className="size-3 shrink-0" />
                  </Button>
                </div>
              </Card>
            )}
          </>
        )}
      </div>
      <div
        className={cn(
          "flex flex-col items-start justify-between",
          "md:flex-row md:items-center md:gap-4",
        )}
      >
        {machineField && <FieldDevices machineField={machineField} />}

        <div className="flex gap-2 mt-4 md:mt-0">
          {machineField.currentGame && (
            <Link to={getScorekeepUrl(machineField.machineUuid, eventType)}>
              <Button size="sm" variant="secondary-flat-light-outline">
                Scorekeep
              </Button>
            </Link>
          )}

          <Link
            to={ROUTES.MANAGE_FIELD.replace(
              ":complexName",
              complexName!,
            ).replace(":fieldId", machineField.machineUuid)}
          >
            <Button size="sm" variant="secondary-flat-light">
              Manage
            </Button>
          </Link>
        </div>
      </div>
    </Card>
  );
}

export { FieldCard };
